// src/pages/Landing.jsx
// Página de presentación pública — reemplaza start.asp / start-en.asp
// Usa los mismos assets CSS del template original (assets1/)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';

const ES = {
  appTitle:    'Recibido - Gestión de Documentos Digitales',
  title:       'Plataforma Digital de Documentación',
  subtitle:    'Acceso seguro y ágil para su organización',
  login:       'Ingresar',
  contact:     'Contacto',
  contactTitle: 'Contacto',
  contactDesc: 'Para mayor información no dude en contactarse con nosotros. Puede utilizar nuestro mail o WhatsApp. Puede también enviarnos un mensaje, para lo cual le rogamos no olvide dejarnos su información de contacto. En todos los casos responderemos a su inquietud a la brevedad.',
  labelName:   'Nombre',
  labelMail:   'Mail',
  labelTopic:  'Tema',
  labelMsg:    'Mensaje',
  send:        'Enviar mensaje',
  sent:        'Su mensaje ha sido enviado',
  errorFields: 'Complete todos los campos',
  slides:      'Presentación',
};

const EN = {
  appTitle:    'Recibido - Digital Document Management',
  title:       'Digital Documents Platform',
  subtitle:    'Easy and secure access for your organization',
  login:       'Login',
  contact:     'Contact',
  contactTitle: 'Contact',
  contactDesc: 'For additional information please contact us via email or WhatsApp. You can also send us a message and we will get back to you shortly.',
  labelName:   'Name',
  labelMail:   'Mail',
  labelTopic:  'Topic',
  labelMsg:    'Message',
  send:        'Send message',
  sent:        'Message sent',
  errorFields: 'Please fill in all fields',
  slides:      'Presentation',
};

export default function Landing() {
  const navigate = useNavigate();
  const lang     = navigator.language?.substring(0, 2).toLowerCase();
  const tx       = lang === 'en' ? EN : ES;

  const [form, setForm]         = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent]         = useState(false);
  const [formError, setFormError] = useState('');

  // Cargar CSS y JS del template de forma imperativa para que React no los gestione
  useEffect(() => {
    // Ajuste de hero para header fixed
    if (!document.getElementById('landing-hero-style')) {
      const st = document.createElement('style');
      st.id = 'landing-hero-style';
      st.textContent = '#hero { padding-top: 72px; }';
      document.head.appendChild(st);
    }

    const cssFiles = [
      { id: 'landing-css-aos',     href: '/assets1/vendor/aos/aos.css' },
      { id: 'landing-css-boxicons',href: '/assets1/vendor/boxicons/css/boxicons.min.css' },
      { id: 'landing-css-glightbox',href: '/assets1/vendor/glightbox/css/glightbox.min.css' },
      { id: 'landing-css-swiper',  href: '/assets1/vendor/swiper/swiper-bundle.min.css' },
      { id: 'landing-css-style',   href: '/assets1/css/style.css' },
    ];
    cssFiles.forEach(({ id, href }) => {
      if (document.getElementById(id)) return;
      const l = document.createElement('link');
      l.id   = id;
      l.rel  = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
    });

    // Cargar solo AOS (sin main.js para evitar conflictos con el DOM de React)
    const aosScript = '/assets1/vendor/aos/aos.js';
    if (!document.querySelector(`script[src="${aosScript}"]`)) {
      const s = document.createElement('script');
      s.src = aosScript;
      s.onload = () => { if (window.AOS) window.AOS.init({ duration: 600 }); };
      document.body.appendChild(s);
    } else if (window.AOS) {
      window.AOS.init({ duration: 600 });
    }

    return () => {
      // Limpiar CSS al desmontar para no afectar otras páginas
      cssFiles.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.parentNode?.removeChild(el);
      });
      const heroStyle = document.getElementById('landing-hero-style');
      if (heroStyle) heroStyle.parentNode?.removeChild(heroStyle);
    };
  }, []);

  const handleChange = (e) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setFormError(tx.errorFields);
      return;
    }
    try {
      await authApi.contact({
        nombre:  form.name,
        email:   form.email,
        tema:    form.subject,
        mensaje: form.message,
      });
      setSent(true);
    } catch {
      setFormError(tx.errorFields);
    }
  };

  return (
    <>
      {/* Header */}
      <header id="header" className="fixed-top">
        <div className="container d-flex align-items-center">
          <a href="/" className="logo me-auto d-flex align-items-center">
            <img src="/images/logo00.png" alt="" />
            <h3 className="sitename" style={{ color: '#fff', margin: 0 }}>&nbsp;&nbsp;{tx.appTitle}&nbsp;&nbsp;&nbsp;</h3>
          </a>
          <nav id="navbar" className="navbar">
            <ul>
              <li><a className="nav-link scrollto" href="#contact">{tx.contact}</a></li>
              <li>
                <a className="getstarted" href="/login"
                  onClick={e => { e.preventDefault(); navigate('/login'); }}>
                  {tx.login}
                </a>
              </li>
            </ul>
            <i className="bi bi-list mobile-nav-toggle"></i>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="hero" className="d-flex align-items-center">
        <div className="container">
          <div className="row">
            <div
              className="col-lg-6 d-flex flex-column justify-content-center pt-4 pt-lg-0 order-2 order-lg-1"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <h1>{tx.title}</h1>
              <h2>{tx.subtitle}</h2>
              <div className="d-flex justify-content-center justify-content-lg-start">
                <a href="/login" className="btn-get-started"
                  onClick={e => { e.preventDefault(); navigate('/login'); }}>
                  {tx.login}
                </a>
                &nbsp;&nbsp;
                <a
                  href={lang === 'en' ? '/presentation.html' : '/presentacion.html'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-watch-video"
                >
                  <i className="bi bi-easel2"></i>
                  <span>{tx.slides}</span>
                </a>
              </div>
            </div>
            <div
              className="col-lg-6 order-1 order-lg-2 hero-img"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <img src="/images/hero-img-green.png" className="img-fluid animated" alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="contact">
        <div className="container" data-aos="fade-up">
          <div className="section-title">
            <h2>{tx.contactTitle}</h2>
            <p>{tx.contactDesc}</p>
          </div>
          <div className="row">
            <div className="col-lg-5 d-flex align-items-stretch">
              <div className="info">
                <div className="email">
                  <i className="bi bi-envelope"></i>
                  <h4>Email:</h4>
                  <p>info@recibido.com.ar</p>
                </div>
                <div className="phone">
                  <i className="bi bi-whatsapp"></i>
                  <h4>WhatsApp:</h4>
                  <p>+54 9 341 502-0405</p>
                </div>
              </div>
            </div>

            <div className="col-lg-7 mt-5 mt-lg-0 d-flex align-items-stretch">
              {sent ? (
                <div className="php-email-form w-100 d-flex align-items-center justify-content-center">
                  <div className="sent-message d-block">{tx.sent}</div>
                </div>
              ) : (
                <form className="php-email-form" onSubmit={handleSend}>
                  <div className="row">
                    <div className="form-group col-md-6">
                      <label>{tx.labelName}</label>
                      <input type="text" name="name" className="form-control"
                        value={form.name} onChange={handleChange} />
                    </div>
                    <div className="form-group col-md-6">
                      <label>{tx.labelMail}</label>
                      <input type="email" name="email" className="form-control"
                        value={form.email} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{tx.labelTopic}</label>
                    <input type="text" name="subject" className="form-control"
                      value={form.subject} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>{tx.labelMsg}</label>
                    <textarea name="message" className="form-control" rows={10}
                      value={form.message} onChange={handleChange} />
                  </div>
                  <div className="my-3">
                    {formError && <div className="error-message d-block">{formError}</div>}
                  </div>
                  <div className="text-center">
                    <button type="submit">{tx.send}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <a href="#" className="back-to-top d-flex align-items-center justify-content-center">
        <i className="bi bi-arrow-up-short"></i>
      </a>
    </>
  );
}
